import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import authApi from "../../../service/IdApi";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import { Column } from "primereact/column";
import { Button } from 'primereact/button';
import { useAppContext } from "../../../context/appContext";
import { Dialog } from "primereact/dialog";
import { Checkbox } from "primereact/checkbox";
import DataUtils from "../../../utils/DataUtils";
import { Menu } from 'primereact/menu';
import CreditHistoryDialog from "./CreditHistoryDialogComp";
import AddCreditsDialog from "./AddCreditsDialogComp";    

const PaidSubscriptions = () => {

    const { switchUnclaimedProfile } = useAppContext();
    const [providers, setProviders] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [subscriptionTableState, setSubscriptionTableState] = useState(() => {
        return {
            first: 0,
            rows: 25,
            page: 1,
            sortField: "nextBillAt",
            sortOrder: 1
        };
    });
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [statusPaid, setStatusPaid] = useState(true);
    const [statusCancelAtEnd, setStatusCancelAtEnd] = useState(true);
    const [users, setUsers] = useState([]);
    const menu = useRef(null);
    const [rowActions, setRowActions] = useState([]);
    const [showAddCreditsDialog, setShowAddCreditsDialog] = useState(false);
    const [selectedProviderId, setSelectedProviderId] = useState(null);
    const [isDialogVisible, setDialogVisible] = useState(false);

    const { isLoading, isFetching, isError, data, refetch } = useQuery(["PaidSubscriptions", subscriptionTableState], async () => {
        var body = {};
        body.first = subscriptionTableState.first;
        body.rows = subscriptionTableState.rows;
        body.sortField = subscriptionTableState.sortField;
        body.sortOrder = subscriptionTableState.sortOrder;
        body.status = [];
        if (statusPaid) {
            body.status.push("paid");
        }
        if (statusCancelAtEnd) {
            body.status.push("cancelAtEnd");
        }
        if (!statusPaid && !statusCancelAtEnd) {
            body.status.push("paid");
            body.status.push("cancelAtEnd");
        }
        const result = await authApi.post("admin/provider/search-subscriptions", body);
        setProviders(result.data.subscriptions);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
        return data;
    });

    const pageHeader = (
        <PageHeading pageTitle="Admin - Active Subscriptions">
            <div className="block">
            </div>
        </PageHeading>
    );

    useEffect(() => {
        refetch();
    }, [statusPaid, statusCancelAtEnd, refetch]);

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const handleShowDialog = (providerId) => {
        setSelectedProviderId(providerId);
        setDialogVisible(true);
    };

    const handleHideDialog = () => {
        setDialogVisible(false);
    };

    const onPage = (event) => {
        setSubscriptionTableState(event);
    };

    const onSort = (event) => {
        setSubscriptionTableState(event);
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const nameClicked = (e) => {
        e.preventDefault();
        switchUnclaimedProfile(selectedProvider.provider.name, selectedProvider.providerId, null);
    }

    const cycleBodyTemplate = (rowData) => {
        return (
            <span>{rowData.cycle === 'm' ? 'Monthly' : rowData.cycle === 'y' ? 'Annually' : rowData.cycle}</span>
        );
    };
    const nameBodyTemplate = (rowData) => {
        return (
            <div className="">
                {rowData.provider && (
                    <a href="#" onClick={(e) => rowClicked(e, rowData)}>
                        {rowData.provider.name}
                    </a>
                )}
                {!rowData.provider && (
                    <span>[{rowData.providerId}]</span>
                )}
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        return (
            <span>
                {rowData.status === "paid" ? "Active" :
                    rowData.status === "cancelAtEnd" ? "Canceling" :
                        rowData.status}
            </span>
        );
    }

    const amountBodyTemplate = (rowData) => {
        var amount = "";
        if (rowData.entitlements) {
            const baseEntitlement = rowData.entitlements.find(entitlement => entitlement.type === "base");
            if (baseEntitlement) {
                amount = DataUtils.toUSCurrencyRounded((baseEntitlement.userCount * baseEntitlement.amount) / 100);
            }
        }
        return (
            <span>{amount}</span>
        );
    }

    const promoExpiresAtBodyTemplate = (rowData) => {
        var display = "";
        if (rowData.entitlements) {
            const promoEntitlement = rowData.entitlements.find(entitlement => entitlement.type === "promo");
            if (promoEntitlement) {
                display = new Date(promoEntitlement.expiresAt).toLocaleDateString();
            }
        }
        return (
            <span>{display}</span>
        );
    }

    const promoBodyTemplate = (rowData) => {
        var amount = "";
        if (rowData.entitlements) {
            const promoEntitlement = rowData.entitlements.find(entitlement => entitlement.type === "promo");
            if (promoEntitlement) {
                const isPromoValid = new Date(promoEntitlement.expiresAt) > new Date();
                if (isPromoValid) {
                    if (promoEntitlement.promoType === "user") {
                        amount = "-" + DataUtils.toUSCurrencyRounded((promoEntitlement.promoUserCount * 1999) / 100);
                    }
                    else if (promoEntitlement.promoType === "flat") {
                        amount = DataUtils.toUSCurrencyRounded((promoEntitlement.amount) / 100);
                    }
                }
            }
        }
        return (
            <span>{amount} </span>
        );
    }

    const creditsBodyTemplate = (rowData) => {
        var amount = "";
        if (rowData.credits) {
            amount = DataUtils.toUSCurrencyRounded(rowData.credits);
        }
        return (
            <span>{amount} </span>
        );
    }

    const rowClicked = async (e, rowData) => {
        e.preventDefault();
        setSelectedProvider(rowData);
        var body = {};
        body.providerId = rowData.providerId;
        const result = await authApi.post("admin/provider/users", body);
        setUsers(result.data.users);
        setShowDetailsDialog(true);
    }

    const hideDetailsDialog = () => {
        setShowDetailsDialog(false);
    }

    const statusPaidChanged = (e) => {
        setStatusPaid(e.checked);
    }

    const statusCancelAtEndChanged = (e) => {
        setStatusCancelAtEnd(e.checked);
    }

    const subscriptionStartAtBodyTemplate = (rowData) => {
        return (
            <span>{new Date(rowData.subscriptionStartAt).toLocaleDateString()}</span>
        );
    }

    const nextBillAtBodyTemplate = (rowData) => {
        return (
            <span>{new Date(rowData.nextBillAt).toLocaleDateString()}</span>
        );
    }

    const emailBodyTemplate = (rowData) => {
        return (
            <div>
                <a href={`/account/admin/user-events?email=${rowData.email}`}>
                    {rowData.email}
                </a>
            </div>
        );
    }

    const showActionMenu = (e, rowData) => {
        const addCredits = {
            label: 'Add Credits',
            icon: 'pi pi-dollar',
            command: () => {
                setSelectedProviderId(rowData.providerId);
                setShowAddCreditsDialog(true);
            }
        };

        const viewCreditHistory = {
            label: 'View Credit History',
            icon: 'pi pi-history',
            command: () => {
                handleShowDialog(rowData.providerId);
            }
        };

        var _rowActions = []
        _rowActions.push(addCredits);
        _rowActions.push(viewCreditHistory);

        setRowActions(_rowActions);
        setSelectedProvider(rowData);
        menu.current.toggle(e);
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions">
                <Button
                    icon="pi pi-bars"
                    className="p-button-rounded p-button-icon-only p-button-text text-800"
                    onClick={(e) => showActionMenu(e, rowData)}
                    aria-controls="popup_menu"
                    aria-haspopup
                    tooltip="Profile Actions"
                    tooltipOptions={{ showDelay: 1000 }}
                />
            </div>
        );
    };

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full">
                        <div className="flex flex-row">
                            <div className="flex align-items-center ml-2">
                                Type
                            </div>
                            <div className="flex align-items-center">
                                <Checkbox className="ml-3 mr-2" inputId="statusPaid" name="statusPaid" onChange={e => statusPaidChanged(e)} checked={statusPaid} />
                                <label htmlFor="statusPaid">Active</label>
                                <Checkbox className="ml-3 mr-2" inputId="statusCancelAtEnd" name="statusCancelAtEnd" onChange={e => statusCancelAtEndChanged(e)} checked={statusCancelAtEnd} />
                                <label htmlFor="statusCancelAtEnd">Cancel at End</label>
                            </div>
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                            <Button type="button" icon="pi pi-file-export" rounded onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    <Menu model={rowActions} popup ref={menu} className="white-space-nowrap" style={{ width: "325px" }} />
                    <DataTable
                        ref={dataTable}
                        value={providers}
                        dataKey="providerId"
                        paginator
                        rows={subscriptionTableState.rows}
                        rowsPerPageOptions={[25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} providers"
                        emptyMessage="No providers found."
                        lazy
                        first={subscriptionTableState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={subscriptionTableState.sortField}
                        sortOrder={subscriptionTableState.sortOrder}
                        loading={isLoading || isFetching || !loaded}
                    >
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column body={nameBodyTemplate} field="name" header="Name" sortable />
                        <Column field="status" body={statusBodyTemplate} header="Status" sortable />
                        <Column field="subscriptionStartAt" body={subscriptionStartAtBodyTemplate} header="Started On" sortable />
                        <Column field="cycle" body={cycleBodyTemplate} header="Billing Cycle" sortable />
                        <Column field="nextBillAt" body={nextBillAtBodyTemplate} header="Next Billing Date" sortable />
                        <Column header="Amount" body={amountBodyTemplate} className="text-right" headerClassName="right-aligned-header" />
                        <Column header="Credits" body={creditsBodyTemplate} className="text-right" headerClassName="right-aligned-header" />
                        <Column header="Promo" body={promoBodyTemplate} className="text-right" headerClassName="right-aligned-header" />
                        <Column header="Promo Expires On" body={promoExpiresAtBodyTemplate} className="text-right" headerClassName="right-aligned-header" />
                    </DataTable>
                </div>
                <Dialog
                    visible={showDetailsDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Subscription Details"
                    modal
                    onHide={hideDetailsDialog}
                    className="p-fluid"
                >
                    {selectedProvider && (
                        <div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Name</label>
                                <div className="col">
                                    <a href="#" onClick={(e) => nameClicked(e)}>
                                        {selectedProvider.provider.name}<span className="ml-2 pi pi-arrow-right-arrow-left" style={{ fontSize: '.8rem' }} />
                                    </a>
                                </div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Provider ID</label>
                                <div className="col">{selectedProvider.providerId}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Provider Number</label>
                                <div className="col">{selectedProvider.provider.providerNumber}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Created On</label>
                                <div className="col">{selectedProvider.createdAt}</div>
                            </div>
                            {selectedProvider.provider && (
                                <>
                                    <div className="field grid mb-0">
                                        <label className="col-fixed" style={{ width: '150px' }}>City</label>
                                        <div className="col">{selectedProvider.provider.city}</div>
                                    </div>
                                    <div className="field grid mb-0">
                                        <label className="col-fixed" style={{ width: '150px' }}>State</label>
                                        <div className="col">{selectedProvider.provider.stateName}</div>
                                    </div>
                                    <div className="field grid mb-0">
                                        <label className="col-fixed" style={{ width: '150px' }}>Zip Code</label>
                                        <div className="col">{selectedProvider.provider.zipCode}</div>
                                    </div>
                                    <div className="field grid mb-0">
                                        <label className="col-fixed" style={{ width: '150px' }}>Country</label>
                                        <div className="col">{selectedProvider.provider.countryName}</div>
                                    </div>
                                    <div className="field grid mb-0">
                                        <label className="col-fixed" style={{ width: '150px' }}>Timezone</label>
                                        <div className="col">{selectedProvider.provider.timezone}</div>
                                    </div>
                                </>
                            )}
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Users</label>
                            </div>
                            <DataTable
                                value={users}
                                dataKey="userId"
                                className="datatable-responsive"
                            >
                                <Column field="userId" header="ID" sortable />
                                <Column body={emailBodyTemplate} field="email" header="Email" sortable />
                                <Column field="createdAt" header="Created On" sortable />
                            </DataTable>
                        </div>

                    )}
                </Dialog>
                
                <AddCreditsDialog
                    visible={showAddCreditsDialog}
                    onHide={() => setShowAddCreditsDialog(false)}
                    providerId={selectedProviderId}
                    refetch={refetch}
                />
                
                <CreditHistoryDialog
                    visible={isDialogVisible}
                    onHide={handleHideDialog}
                    providerId={selectedProviderId}
                />
            </div>
        </div>
    );
};

export default PaidSubscriptions;
